import {
  DateTime,
  DateUnit,
  NaiveDate,
  TimeOfDay,
  TimezoneRegion,
  Utc,
} from "@tento-chrono";
import { ChartViewModel } from "@tento-lona/platform";
import { Graph, type CellAdapter, DATA_FACADE, type Option } from "@tento-lona/sheets";
import { type GroupedCellAdapter } from "@tento-lona/sheets/platform";
import { ChartPointerStore, GraphData, rebuildDataFacade } from "@tento-lona/sheets/internal-ui";
import { REGRESSION_FACADE } from "@tento-lona/sheets/platform";
import type { CellEntry, TimepointCellEntry, RowLocalId, DataFacade } from "@tento-lona/sheets";
import { SheetViewModel, type SheetRow } from "@tento-lona";
import { NumRangeLike } from "@tento-core/range";
import { LineChartBody } from "@tento-ui/components/line-chart/line-chart-body";
import { Utils } from "@tento-ui/utils";
import { LiveData } from "@tento-core/livedata";
import { createRenderer } from "@tento-ui/components/data/renderer-factory";
import {
  createSeriesRenderer,
  type CanvasSeriesRenderer,
  type RendererAttributes,
} from "@tento-data-ui";
import {
  plotFrameFromDataFrame,
  type PlotFrame,
} from "./plot-frame.ts";
import type { RegressionFacade } from "@tento-lona/sheets/platform";
import { liveVariantOf } from "../../../sheet/live-variant.ts";
import type { SheetBinder } from "../../../sheet/sheet-index-renderer.ts";

export class DataStore {
  #vms: Map<string, ChartViewModel> = new Map();
  #datas: Map<RowLocalId, GraphData.Collection> = new Map();

  /**
   * Per-chart y-scale (`vm` + `yScaler`), keyed by the data alias row id.
   * The canvas builds it from the auto-fit extent; the data legend reuses
   * it to render `std-line-chart-legend` on the SAME scale as the candles,
   * and the same `yScaler` is what the canvas paints against — so dragging
   * the legend rescales the candles. `autoFit` is the last auto-fit range
   * applied; while it is unchanged the existing `yScaler` is preserved so a
   * manual drag survives re-binds.
   */
  #chartScales: Map<string, {
    vm: ChartViewModel;
    yScaler: ChartViewModel.YScaler;
    min: number;
    max: number;
  }> = new Map();

  /**
   * Resolve the shared `yScaler` for `aliasRowId` from the auto-fit
   * `extent`. Reuses the cached scaler (preserving any manual drag) while
   * the auto-fit extent is unchanged; otherwise builds a fresh scaler
   * seeded with the extent (clean ticks). Returns `changed` so the caller
   * can re-bind the legend.
   */
  resolveChartScale(
    aliasRowId: RowLocalId,
    vm: ChartViewModel,
    extent: { min: number; max: number },
  ): { yScaler: ChartViewModel.YScaler; changed: boolean } {
    const key = aliasRowId.toString();
    const prev = this.#chartScales.get(key);
    if (prev && prev.vm === vm && prev.min === extent.min && prev.max === extent.max) {
      return { yScaler: prev.yScaler, changed: false };
    }
    const range = {
      min: extent.min,
      max: extent.max,
      center: (extent.min + extent.max) / 2,
    } as ChartViewModel.AxisRange;
    const yScaler = new ChartViewModel.LinearYScaler({ range }, null);
    this.#chartScales.set(key, { vm, yScaler, min: extent.min, max: extent.max });
    return { yScaler, changed: true };
  }

  /** The shared `{vm, yScaler}` for `aliasRowId`, or `null` if not computed. */
  chartScaleFor(
    aliasRowId: RowLocalId,
  ): Option<{ vm: ChartViewModel; yScaler: ChartViewModel.YScaler }> {
    const s = this.#chartScales.get(aliasRowId.toString());
    return s ? { vm: s.vm, yScaler: s.yScaler } : null;
  }

  #debugGraphData: Option<GraphData.Collection> = null;
  getGraphDataForDebug(): GraphData.Collection {
    if (this.#debugGraphData) return this.#debugGraphData;
    this.#debugGraphData = GraphData.Collection.makeFromSpec(
      [
        {
          key: "default",
          type: "debug",
          yRange: ChartViewModel.AxisRange.HALF_UNIT,
          seed: 42,
        },
      ],
      ChartViewModel.AxisRange.HALF_UNIT,
    );
    return this.#debugGraphData;
  }

  constructor(
    readonly isDebug: LiveData.Flag.Readable,
  ) {
    isDebug.addListener(this, () => {
      this.#vms = new Map();
      this.#datas = new Map();
      this.#debugGraphData = null;
    });
  }

  #getGraphData(
    row: SheetRow.Joined.Handle,
    dataFacade: Option<DataFacade>,
    isDebug: boolean,
  ): GraphData.Collection {
    let cached = this.#datas.get(row.id);
    if (cached) return cached;

    if (isDebug) {
      const yAxis = dataFacade?.graphProps()?.yAxis as Option<ChartViewModel.AxisRange>;
      cached = GraphData.Collection.makeFromSpec(
        [
          {
            key: "default",
            type: "debug",
            yRange: ChartViewModel.AxisRange.HALF_UNIT,
            seed: Utils.hash(row.id.toString()),
          },
        ],
        yAxis ?? ChartViewModel.AxisRange.HALF_UNIT,
      );
    } else {
      // Build collection specs from DataIndex series
      const specs: GraphData.Collection.Spec[] = [];
      if (dataFacade) {
        for (const [, series] of dataFacade.peekAllSeries()) {
          if (series.type === "regression") {
            specs.push({
              key: series.key,
              type: "regression",
              yRange: series.yRange as Option<ChartViewModel.AxisRange>,
              convHalfWidth: series.convHalfWidth,
              maxGap: series.maxGap,
            });
          } else {
            specs.push({
              key: series.key,
              type: "default",
              yRange: series.yRange as Option<ChartViewModel.AxisRange>,
            });
          }
        }
      }
      if (specs.length === 0) {
        specs.push({
          key: "default",
          type: "regression",
          yRange: ChartViewModel.AxisRange.HALF_UNIT,
        });
      }
      const yAxis = dataFacade?.graphProps()?.yAxis as Option<ChartViewModel.AxisRange>;
      cached = GraphData.Collection.makeFromSpec(
        specs,
        yAxis ?? ChartViewModel.AxisRange.HALF_UNIT,
      );
    }

    this.#datas.set(row.id, cached);
    return cached;
  }

  #getVm(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
  ): ChartViewModel {
    const key = `${sheet.id}__${row.id}`;
    let vm = this.#vms.get(key);
    if (vm) return vm;
    const dataFacade = row.as(DATA_FACADE);
    const graphData = this.#getGraphData(row, dataFacade, this.isDebug.get());
    vm = new ChartViewModel(
      new ChartPointerStore(),
      graphData,
      false,
      null,
    );
    this.#vms.set(key, vm);
    return vm;
  }

  getVmForRow(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
  ): ChartViewModel {
    return this.#getVm(sheet, row);
  }

  async getAllInRange(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
    pdtz: NaiveDate.Partial.WithTz,
    renderersFromChildren: readonly CanvasSeriesRenderer[],
  ): Promise<DataStore.DataFrame> {
    const [pd, tz] = pdtz;
    return this.#getAllInDateRange(
      sheet,
      row,
      pd.toRange(),
      tz,
      renderersFromChildren,
    );
  }

  /**
   * Row-spanning variant (B0): load + build ONE `PlotFrame` over the full
   * visible date `range` with a single continuous `xRange`, so a single canvas
   * can paint the whole row with uniform px/day — no per-column slope shear and
   * no per-column view/data-window desync. The per-column `getAllInRange` above
   * is a thin wrapper that passes one column's `pd.toRange()`.
   */
  async getAllInRangeSpanning(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
    range: NaiveDate.Range,
    tz: TimezoneRegion,
    renderersFromChildren: readonly CanvasSeriesRenderer[],
  ): Promise<DataStore.DataFrame> {
    return this.#getAllInDateRange(sheet, row, range, tz, renderersFromChildren);
  }

  async #getAllInDateRange(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
    range: NaiveDate.Range,
    tz: TimezoneRegion,
    renderersFromChildren: readonly CanvasSeriesRenderer[],
  ): Promise<DataStore.DataFrame> {
    const isDebug = this.isDebug.get();

    const defaultPadding: DateUnit.Parts.Opt = { days: 14 };

    const dataFacade = row.as(DATA_FACADE);
    const graphProps = dataFacade?.graphProps();

    // xRange parameters
    const padding: DateUnit.Parts.Opt = graphProps?.xPadding ?? defaultPadding;
    const paddedRange = new NaiveDate.Range(
      range.start.sub(padding),
      range.end.add(padding),
    );
    const dtr = new DateTime.Range(
      tz.toWallClock(paddedRange.start.withTime(TimeOfDay.ZERO)),
      tz.toWallClock(paddedRange.end.withTime(TimeOfDay.ZERO)),
    );

    const rowsVariant = liveVariantOf(row);
    if (!isDebug && rowsVariant != null) {
      await rowsVariant.ensureLoaded(paddedRange, null);
      if (dataFacade != null) rebuildDataFacade(dataFacade);
    } else {
      const rh = row.joined.rowHandle;
      if (rh) {
        await rh.ensureFresh(paddedRange);
      }
    }

    // Fetch raw cells (still needed for data-json display)
    const [dated, timed] = await Promise.all([
      row.cells(paddedRange),
      row.cells(dtr.toUtc()),
    ]);
    const ent = dated.filter((c) => c.kind === "date");
    const customEnts = timed.filter((c) => c.kind === "timepoint");

    // Compute x-ranges
    const toXRange = isDebug
      ? DataStore.ToXRange.BY_HOURS
      : (graphProps?.toXRange ?? DataStore.ToXRange.BY_DSE);
    const xRange = toXRange(range, tz);
    const paddedXRange = toXRange(paddedRange, tz);

    // Populate GraphData from DataIndex points
    const vm = this.#getVm(sheet, row);

    const xDisplayRaw = graphProps?.xDisplay;
    vm.xDisplay = xDisplayRaw ? (x: number) => xDisplayRaw(x, tz) : null;

    // X-axis ticks are placed by the preset, not the SDK: evaluate its
    // `xTicks` closure once here against the same visible `xRange` the
    // series renderers map through, and freeze the result into the plot
    // frame below so the canvas draws exactly these positions.
    const xTicksFn = graphProps?.xTicks;
    const xTicks: Option<readonly number[]> = xTicksFn ? xTicksFn(xRange) : null;

    // The now-cursor is placed the same way and for the same reason: the
    // preset knows where "now" is in its own domain, the SDK does not.
    // Evaluated HERE, per frame build, so a live preset's cursor advances on
    // every repaint exactly as its sliding domain does — a frozen historical
    // preset declares no closure and gets no cursor.
    const xNowFn = graphProps?.xNow;
    const xNow: Option<number> = xNowFn ? xNowFn() : null;

    // Build regression lookup from child data-series RegressionFacade instances.
    // This is the single source of truth — the UI no longer computes its own.
    const regressionByKey = new Map<string, RegressionFacade>();
    if (!isDebug) {
      for (const child of row.children()) {
        const facade = child.as(REGRESSION_FACADE);
        if (facade?.seriesKey && facade.regression) {
          regressionByKey.set(facade.seriesKey, facade);
        }
      }
    }

    const populatePromises: Promise<GraphData>[] = [];
    for (const [key, { data }] of vm.graphData.rows) {
      const indexed = dataFacade?.peekSeries(key);
      const points: [number, any][] = indexed && !isDebug
        ? (indexed.points as [number, any][])
        : [];
      const regCurve = regressionByKey.get(key)?.regression?.curve;
      populatePromises.push(
        data.populate(xRange, paddedXRange, points, row.id.toString(), regCurve),
      );
    }
    await Promise.all(populatePromises);

    const indexedFrame = !isDebug ? dataFacade?.peekDataFrame(paddedRange, tz) : null;
    const plotFrame = indexedFrame
      ? plotFrameFromDataFrame(indexedFrame, xRange, xTicks, xNow)
      : null;
    let plotRenderer: CanvasSeriesRenderer[] = [];
    if (!isDebug && plotFrame !== null && plotFrame !== undefined) {
      plotRenderer = renderersFromChildren.length > 0
        ? [...renderersFromChildren]
        : DataStore.#buildPlotRenderers(graphProps?.renderers ?? []);
    }

    // Build legacy renderers. Prefer the discovered set from the layout's
    // child rows (contentProviders.canvas); fall back to the
    // legacy `graphProps.renderers` representation when no children
    // claimed the layout.
    let renderers: LineChartBody.Renderer[];
    if (isDebug) {
      renderers = LineChartBody.Renderer.DEFAULT;
    } else if (graphProps != null) {
      renderers = DataStore.#buildRenderers(graphProps.renderers);
    } else {
      // Preset not resolved yet (hydration race: the data facade / lookupKey
      // is not yet available, so peekDataFrame returned null and plotFrame is
      // null). Draw nothing rather than the generic red regression default
      // from buildRenderers([]) — that default otherwise flickers a red line
      // into every data chart on load until the facade resolves and the real
      // plotFrame/renderers take over.
      renderers = [];
    }

    return {
      cells: ent,
      customCells: customEnts,
      xRange: Object.freeze(xRange),
      vm,
      renderer: renderers,
      plotFrame,
      plotRenderer,
    };
  }

  // ── Renderer factory ──────────────────────────────────────────────

  static #buildRenderers(
    specs: readonly Graph.RendererSpec[],
  ): LineChartBody.Renderer[] {
    if (specs.length === 0) {
      return [
        new LineChartBody.Renderer.Points("default", 1),
        new LineChartBody.Renderer.HRule(),
        new LineChartBody.Renderer.RegressionBand(),
        new LineChartBody.Renderer.RegressionCurve(),
      ];
    }
    const renderers: LineChartBody.Renderer[] = [];
    for (const rs of specs) {
      const attrs: Record<string, unknown> = { type: rs.type };
      if (rs.id != null) attrs["id"] = rs.id;
      if (rs.key != null) attrs["key"] = rs.key;
      if (rs.color != null) attrs["color"] = rs.color;
      if (rs.radius != null) attrs["radius"] = rs.radius;
      if (rs.offset != null) attrs["offset"] = rs.offset;
      if (rs.offsetType != null) attrs["offsetType"] = rs.offsetType;
      if (rs.center != null) attrs["center"] = rs.center;
      if (rs.barColor != null) attrs["barColor"] = rs.barColor;
      if (rs.topColor != null) attrs["topColor"] = rs.topColor;
      if (rs.bottomColor != null) attrs["bottomColor"] = rs.bottomColor;
      const r = createRenderer(attrs);
      if (r) renderers.push(r);
    }
    return renderers;
  }

  static #buildPlotRenderers(
    specs: readonly Graph.RendererSpec[],
  ): CanvasSeriesRenderer[] {
    const renderers: CanvasSeriesRenderer[] = [];
    for (const rs of specs) {
      const r = createSeriesRenderer(DataStore.#rendererAttributes(rs));
      if (r) renderers.push(r);
    }
    return renderers;
  }

  static #rendererAttributes(rs: Graph.RendererSpec): RendererAttributes {
    const attrs: Record<string, string | number | boolean | null> = {
      type: rs.type,
    };
    if (rs.id != null) attrs["id"] = rs.id;
    if (rs.key != null) attrs["key"] = rs.key;
    if (rs.color != null) attrs["color"] = rs.color;
    if (rs.radius != null) attrs["radius"] = rs.radius;
    if (rs.offset != null) attrs["offset"] = rs.offset;
    if (rs.offsetType != null) attrs["offsetType"] = rs.offsetType;
    if (rs.center != null) attrs["center"] = rs.center;
    if (rs.barColor != null) attrs["barColor"] = rs.barColor;
    if (rs.topColor != null) attrs["topColor"] = rs.topColor;
    if (rs.bottomColor != null) attrs["bottomColor"] = rs.bottomColor;
    return attrs;
  }
}

export namespace DataStore {
  export type ToXRange = (
    r: NaiveDate.Range,
    tz: TimezoneRegion,
  ) => NumRangeLike;
  export namespace ToXRange {
    export const BY_HOURS: ToXRange = (r): NumRangeLike => ({
      start: r.start.dse * 24,
      end: r.end.dse * 24,
    });

    export const BY_DSE: ToXRange = (r): NumRangeLike => ({
      start: r.start.dse,
      end: r.end.dse,
    });

    export const BY_MSE: ToXRange = (r, tz): NumRangeLike => ({
      start: tz.toWallClock(r.start.withTime(TimeOfDay.ZERO)).mse,
      end: tz.toWallClock(r.end.withTime(TimeOfDay.ZERO)).mse,
    });
  }

  export type DataFrame = {
    cells: CellEntry[];
    customCells: TimepointCellEntry[];
    xRange: NumRangeLike;
    vm: ChartViewModel;
    renderer: LineChartBody.Renderer[];
    plotFrame: Option<PlotFrame>;
    plotRenderer: CanvasSeriesRenderer[];
  };

  /** One cell's points (the SDK's `CellAdapter`). */
  export type Adapter<Y = number> = CellAdapter<Y>;

  export namespace Adapter {
    /** The adapter that reads no points. */
    export const empty: Adapter = (): null => null;
  }

  /** A row's points from all its cells at once (the SDK's `GroupedCellAdapter`). */
  export type GroupedAdapter<Y = number> = GroupedCellAdapter<Y>;

  export type CompressedActivityEntry = [
    number /* activity_level */,
    number /* timestamp - mse */,
    number /* duration */,
  ];
}
